import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  pricingTiers,
  getTierDisplayName,
  getTierFeatures,
  getButtonText,
} from '../utils/pricingConfig';

const PricingPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleSubscribe = async (tierId: number, tierName: string) => {
    if (!user) {
      navigate('/login');
      return;
    }
  };

  return (
    <div className='overflow-y-auto h-[100vh] bg-gradient-to-br from-gray-50 to-gray-100'>
      <div className='pt-16 pb-32 px-4'>
        <div className='max-w-7xl mx-auto'>
          {/* Header */}
          <div className='text-center mb-16'>
            <h1 className='text-4xl font-bold text-gray-900 mb-4'>
              Choose Your Plan
            </h1>
            <p className='text-xl text-gray-600 max-w-2xl mx-auto'>
              Perfect your coding interview skills with AI-powered practice
              sessions. Choose the plan that fits your preparation timeline.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className='grid md:grid-cols-2 lg:grid-cols-4 gap-8'>
            {pricingTiers.map((tier) => (
              <div
                key={tier.id}
                className={`relative bg-white rounded-2xl shadow-xl border-2 flex flex-col h-full ${
                  tier.popular
                    ? 'border-blue-500 ring-4 ring-blue-100'
                    : 'border-gray-200'
                }`}
              >
                {tier.popular && (
                  <div className='absolute -top-4 left-1/2 transform -translate-x-1/2'>
                    <span className='bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-medium'>
                      Most Popular
                    </span>
                  </div>
                )}

                <div className='p-8 flex flex-col flex-grow'>
                  {/* Plan Name */}
                  <h3 className='text-2xl font-bold text-gray-900 mb-2'>
                    {getTierDisplayName(tier, 'full')}
                  </h3>

                  {/* Price */}
                  <div className='mb-6'>
                    <span className='text-4xl font-bold text-gray-900'>
                      {tier.price}
                    </span>
                    <span className='text-gray-600 ml-1'>
                      {tier.period === 'forever' ? '' : tier.period}
                    </span>
                  </div>

                  {/* Minutes */}
                  <div className='mb-6 p-4 bg-blue-50 rounded-lg'>
                    <div className='text-center'>
                      <div className='text-2xl font-bold text-blue-600'>
                        {tier.minutes} min
                      </div>
                      <div className='text-sm text-blue-600'>per month</div>
                    </div>
                  </div>

                  {/* Features */}
                  <ul className='space-y-3 mb-8 flex-grow'>
                    {getTierFeatures(tier).map((feature, featureIndex) => (
                      <li
                        key={featureIndex}
                        className='flex items-start'
                      >
                        <svg
                          className='w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M5 13l4 4L19 7'
                          />
                        </svg>
                        <span className='text-gray-700 text-sm'>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <button
                    onClick={() => handleSubscribe(tier.id, tier.name)}
                    className={`w-full py-3 px-4 rounded-lg font-medium text-white transition-colors mt-auto ${
                      tier.name === 'Starter'
                        ? 'bg-gray-600 hover:bg-gray-700'
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {getButtonText(tier, 'pricing', !!user)}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* FAQ Section */}
          <div className='mt-20 max-w-4xl mx-auto'>
            <h2 className='text-3xl font-bold text-center text-gray-900 mb-12'>
              Frequently Asked Questions
            </h2>
            <div className='grid md:grid-cols-2 gap-8'>
              <div>
                <h3 className='text-lg font-semibold text-gray-900 mb-2'>
                  How does the minute system work?
                </h3>
                <p className='text-gray-600'>
                  Each practice session uses minutes from your monthly
                  allowance. Sessions typically last 15-30 minutes depending on
                  problem complexity.
                </p>
              </div>
              <div>
                <h3 className='text-lg font-semibold text-gray-900 mb-2'>
                  Can I upgrade or downgrade anytime?
                </h3>
                <p className='text-gray-600'>
                  Yes! You can change your plan at any time. Upgrade will take
                  effect immediately, you will be charged a prorated amount at
                  your next billing cycle. Downgrade take effect at your next
                  billing cycle.
                </p>
              </div>
              <div>
                <h3 className='text-lg font-semibold text-gray-900 mb-2'>
                  What happens to unused minutes?
                </h3>
                <p className='text-gray-600'>
                  Unused minutes expire at the end of each month. We recommend
                  choosing a plan that matches your practice schedule.
                </p>
              </div>
              <div>
                <h3 className='text-lg font-semibold text-gray-900 mb-2'>
                  What happens if I use more minutes than my plan allows?
                </h3>
                <p className='text-gray-600'>
                  If you exceed your monthly minute allowance, the overused
                  minutes will be calculated and charged at your next billing
                  cycle. The rate depends on your current plan.
                </p>
              </div>
              <div>
                <h3 className='text-lg font-semibold text-gray-900 mb-2'>
                  Is there a free trial?
                </h3>
                <p className='text-gray-600'>
                  Yes! The Free plan gives you 15 minutes per month to try our
                  platform and see if it's right for you.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
